import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Loader2, Check, AlertCircle } from "lucide-react";

export default function BookingConfirmationPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [tries, setTries] = useState(0);
  const [status, setStatus] = useState("polling");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (stop) return;
        setInfo(data);
        if (data.status === "complete" && data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (tries >= 10) { setStatus("timeout"); return; }
        setTimeout(() => setTries(t => t + 1), 2000);
      } catch {
        if (tries >= 10) setStatus("error");
        else setTimeout(() => setTries(t => t + 1), 2000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [tries, sessionId]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] py-14" data-testid="booking-confirmation-page">
      <div className="max-w-xl mx-auto px-4">
        <Card className="p-10 text-center">
          {status === "polling" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[color:var(--brand-sky)] animate-spin mb-3" />
              <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Vérification du paiement…</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Ne fermez pas cette page.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
                <Check className="w-8 h-8" />
              </div>
              <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Réservation confirmée !</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Merci. Montant payé : <strong>{((info?.amount_cents || 0) / 100).toFixed(2)} €</strong>. Vous recevrez un email de confirmation.</p>
              {info?.order_id && <div className="text-xs font-mono mt-4 text-[hsl(var(--muted-foreground))]">Réf : {info.order_id}</div>}
              <Link to="/" className="inline-block mt-6 text-[color:var(--brand-sky)] font-semibold underline">Retour à l'accueil</Link>
            </>
          )}
          {(status === "expired" || status === "error" || status === "timeout") && (
            <>
              <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3" />
              <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Paiement non confirmé</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
                {status === "expired" ? "La session a expiré." : status === "timeout" ? "La vérification prend plus de temps que prévu." : "Une erreur est survenue."}
              </p>
              <Link to="/reservation" className="inline-block mt-6 text-[color:var(--brand-sky)] font-semibold underline">Réessayer la réservation</Link>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
